import { DropDetails, WellDisplayMode } from './constants';
import {
  useAllDropImageParameters,
  useAllRegions,
  usePlateImageStructure,
  useSelectedDropImageParameters,
  useUpdateExpRegionCaptureProfiles,
} from 'redux/hooks/experiment';
import { WellDetailsStates } from 'business/models/CustomModels';
import { Props as WellContainerProps } from './WellContainer';
import { getGridDetails } from 'components/grid/utils';
import { EGridLayout, gridColumnGap, gridRowGap } from 'components/grid/constants';
import {
  useFocusLevelImagesAvailableKeys,
  useWellDropImageStoreStatus,
  useWellImagingDetails,
} from 'redux/hooks/well';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import {
  useDropLables,
  useExperimentImagingDisabledState,
  useIsWellImagingEnabled,
} from 'components/Experiment/hooks';
import { fetchImagingDetails } from 'redux/thunks/well';
import {
  appendFocusLevelImagesAvailableKeys,
  getFocusLevelImageStoreByKey,
  removeFocusLevelImagesByKey,
  setFocusLevelImageStoreByKey,
  setImageZoomDto,
  WellImagingDetails,
} from 'redux/reducers/wellReducer';
import { SizeDetails } from 'components/canvas/constants';
import {
  DropImageParameters,
  PlateImageStructure,
  WellNavigationDetails,
} from 'components/Experiment/constants';
import { getNavigatorImageKeysAsString } from 'components/commandpanel/Navigator/utils';
import { EFetchingStatus } from 'enums/EFetchingStatus';
import WellService from 'business/services/WellService';
import {
  DropImageSettingsDTO,
  ExperimentTuningValuesDTO,
  IImageZoomDTO,
  ImageFocusLevelDTO,
} from 'business/models/Models';
import useCurrentRegionIds from 'CustomHooks/useCurrentRegionIds';
import {
  selectExpId,
  selectExpRegionCaptureProfiles,
} from 'redux/selectors/ExperimentStateSelector';
import CaptureProfileService from 'business/services/CaptureProfileService';
import { selectImageZoomVisibleArea } from 'redux/selectors/StateSelectors';
import { debounce } from 'lodash';
import { getRegionIdCached } from './utils';
import { ImageTuningValues } from 'business/models/ImageTuningModel';
import { getCompositeImageStoreImageByKey } from 'redux/reducers/experimentDataReducer';
import {
  getTuningWindowForPlate,
  waitForValue,
} from 'components/commandpanel/ImageTuning/utils';
import { UpdateGlobalDataStoreVersion } from 'business/services/GlobalService';
import { CAPTUREPROFILETUNINGVALUESSTORE } from 'utils/IndexDBGlobal';
import { setImageTuning } from 'redux/reducers/experimentReducer';
import { getWellDropImageUniqueKey } from 'redux/utils/well';

export const useWellList = (
  wellNavigationDetails: WellNavigationDetails,
  displayMode: WellDisplayMode,
  layout: EGridLayout,
  sizeDetails: SizeDetails,
) => {
  const allWellImagingSettingDetails = useAllDropImageParameters();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const dropLables = useDropLables();
  const { wellNum, plateNum, plateID } = wellNavigationDetails || {};
  const wellContainerProps: WellContainerProps[] = useMemo(() => {
    if (displayMode === WellDisplayMode.Drop) {
      // Note: if there is some issue in drop view, we need to select last item from useAllDropImageParameters;
      const { cpId, dropNumber, inspection, regionName } =
        selectedDropImageParameters || {};
      return [
        {
          sizeDetails,
          wellDetails: {
            wellNum,
            plateID,
            plateNum,
            cpID: cpId,
            dropNumber,
            regionName,
            regionID: getRegionIdCached(plateID, wellNum, dropNumber, regionName),
            imageBatchId: inspection?.groupedInspections?.find(
              (gi) => gi.plateId === plateID,
            )?.imageBatchId,
            inspectionSet: inspection,
          } as WellDetailsStates,
        },
      ];
    } else if (displayMode === WellDisplayMode.Well) {
      const { rows, columns } = getGridDetails(dropLables?.length, layout);
      const size = {
        canvasWidth:
          sizeDetails.canvasWidth / columns - (columns > 1 ? columns * gridColumnGap : 0),
        canvasHeight: sizeDetails.canvasHeight / rows - rows * gridRowGap,
      };
      const wellImagingSettingDetails =
        allWellImagingSettingDetails?.[allWellImagingSettingDetails?.length - 1];
      const { cpId, inspection, regionName } = wellImagingSettingDetails || {};
      return dropLables?.map((drop) => ({
        sizeDetails: size,
        wellDetails: {
          wellNum,
          plateID,
          plateNum,
          cpID: cpId,
          regionName: regionName,
          regionID: getRegionIdCached(plateID, wellNum, drop.id, regionName), // here drop.id is dropNumber actually
          imageBatchId: inspection?.groupedInspections?.find(
            (gi) => gi.plateId === plateID,
          )?.imageBatchId,
          inspectionSet: inspection,
          dropNumber: drop.id,
        } as WellDetailsStates,
      }));
    } else if (displayMode === WellDisplayMode.Compare) {
      if (!allWellImagingSettingDetails?.length) {
        return [];
      }
      const { rows, columns } = getGridDetails(
        allWellImagingSettingDetails?.length,
        layout,
      );
      const size = {
        canvasWidth:
          sizeDetails.canvasWidth / columns - (columns > 1 ? columns * gridColumnGap : 0),
        canvasHeight: sizeDetails.canvasHeight / rows - rows * gridRowGap,
      };

      return allWellImagingSettingDetails?.map((wellImagingSettingDetail) => {
        const { cpId, dropNumber, inspection, regionName } =
          allWellImagingSettingDetails?.length > 1
            ? wellImagingSettingDetail
            : selectedDropImageParameters || {};
        return {
          sizeDetails: size,
          wellDetails: {
            wellNum,
            plateID,
            plateNum,
            cpID: cpId,
            dropNumber,
            regionName,
            regionID: getRegionIdCached(plateID, wellNum, dropNumber, regionName),
            imageBatchId: inspection?.groupedInspections?.find(
              (gi) => gi.plateId === plateID,
            )?.imageBatchId,
            inspectionSet: inspection,
          } as WellDetailsStates,
        };
      });
    }
    return wellContainerProps;
  }, [
    wellNavigationDetails,
    displayMode,
    sizeDetails,
    allWellImagingSettingDetails,
    layout,
    selectedDropImageParameters,
  ]);

  return wellContainerProps;
};

export const useFetchWellImagingDetails = (wellDetails: DropDetails) => {
  const { expId, plateID, wellNum, dropNumber, regionID, cpID, imageBatchId } =
    wellDetails;
  const wellImagingDetails = useWellImagingDetails(
    plateID,
    wellNum,
    dropNumber,
    imageBatchId,
    cpID,
    regionID,
  );
  const dispatch = useRMDispatch();

  useEffect(() => {
    if (wellDetails && wellImagingDetails?.status === undefined) {
      dispatch(
        fetchImagingDetails({
          expId,
          plateId: plateID,
          wellNumber: wellNum,
          dropNumber,
          regionId: regionID,
          cpId: cpID,
          imageBatchId,
        }),
      );
    }
  }, [wellDetails]);

  return wellImagingDetails || ({} as WellImagingDetails);
};

export const useFetchFocusLevelData = (
  wellNavigationDetails: WellNavigationDetails,
  dropImageParameters: DropImageParameters,
) => {
  const { plateID, wellNum } = wellNavigationDetails || {};
  const { cpId, dropNumber, inspection, regionName } = dropImageParameters || {};
  const imageBatchId = inspection?.groupedInspections?.find(
    (grp) => grp.plateId === wellNavigationDetails?.plateID,
  )?.imageBatchId;
  const wellService = new WellService();
  const dispatch = useRMDispatch();

  const plateImageStructure = usePlateImageStructure();
  const wellImageStore = useWellDropImageStoreStatus();
  const allRegions = useAllRegions();

  const fetchData = useCallback(async () => {
    const key = getNavigatorImageKeysAsString(wellNavigationDetails, dropImageParameters);
    if (getFocusLevelImageStoreByKey(key)?.status === undefined) {
      setFocusLevelImageStoreByKey(key, {
        status: EFetchingStatus.InProgress,
        data: [],
      });
      try {
        const focusLevelImagesData = await wellService.getDropFocusLevels(
          plateID,
          wellNum,
          imageBatchId,
          dropNumber,
          cpId,
          regionName,
        );
        dispatch(appendFocusLevelImagesAvailableKeys(key));
        setFocusLevelImageStoreByKey(key, {
          status: EFetchingStatus.Loaded,
          data: focusLevelImagesData,
        });
      } catch {
        removeFocusLevelImagesByKey(key);
      }
    }
  }, [
    wellNavigationDetails,
    dropImageParameters,
    plateID,
    wellNum,
    imageBatchId,
    dropNumber,
    cpId,
    regionName,
    dispatch,
  ]);

  const currentWellImageKey = useMemo(() => {
    if (!wellNavigationDetails || !dropImageParameters) {
      return null;
    }

    const { plateID: pid, wellNum: wellNumber } = wellNavigationDetails;
    const {
      cpId: cpID,
      dropNumber: dropNum,
      regionName: regName,
      inspection: insp,
    } = dropImageParameters;

    const batchId = insp?.groupedInspections?.find(
      (grp) => grp.plateId === pid,
    )?.imageBatchId;

    const regionID = getRegionIdCached(pid, wellNumber, dropNum, regName, allRegions);

    if (batchId == null || regionID == null) {
      return null;
    }

    return getWellDropImageUniqueKey({
      pid,
      wellNumber,
      batchId,
      dropNumber: dropNum,
      cpID,
      regionID,
    });
  }, [wellNavigationDetails, dropImageParameters, allRegions]);

  useEffect(() => {
    if (!plateID || !wellNum || !imageBatchId || !dropNumber || !cpId || !regionName) {
      return;
    }

    const focusLevelKey = getNavigatorImageKeysAsString(
      wellNavigationDetails,
      dropImageParameters,
    );
    if (getFocusLevelImageStoreByKey(focusLevelKey)?.status !== undefined) {
      return;
    }

    const hasCurrentWellImage =
      currentWellImageKey &&
      wellImageStore?.[currentWellImageKey]?.fetchingStatus === EFetchingStatus.Loaded;
    if (!hasCurrentWellImage) {
      return;
    }
    const hasCompositeImages = plateImageStructure?.some(
      (plate) => plate.imageCompositeKeyString && plate.imageCompositeKeyString !== '---',
    );

    if (hasCompositeImages) {
      fetchData();
    }
  }, [
    plateImageStructure,
    wellImageStore,
    currentWellImageKey,
    plateID,
    wellNum,
    imageBatchId,
    dropNumber,
    cpId,
    regionName,
    wellNavigationDetails,
    dropImageParameters,
    fetchData,
  ]);
};

export const useFocusLevelImages = (
  wellNavigationDetails: WellNavigationDetails,
  dropImageParameters: DropImageParameters,
): ImageFocusLevelDTO[] => {
  const focusLevelImagesKeys = useFocusLevelImagesAvailableKeys();
  const key = getNavigatorImageKeysAsString(wellNavigationDetails, dropImageParameters);
  if (focusLevelImagesKeys.includes(key)) {
    const imageData = getFocusLevelImageStoreByKey(key);
    if (imageData?.status == EFetchingStatus.Loaded) {
      return imageData.data;
    }
  }

  return [];
};

export const useUpdateBFLinFocusLevelImageStore = () => {
  const focusLevelImagesKeys = useFocusLevelImagesAvailableKeys();
  return (
    wellNavigationDetails: WellNavigationDetails,
    dropImageParameters: DropImageParameters,
    imageId,
  ) => {
    const key = getNavigatorImageKeysAsString(wellNavigationDetails, dropImageParameters);
    if (focusLevelImagesKeys.includes(key)) {
      const imageData = getFocusLevelImageStoreByKey(key);
      imageData.data.forEach((element) => {
        if (element.imageId === imageId) {
          element.isBestFocusLevel = true;
        } else {
          element.isBestFocusLevel = false;
        }
      });
    }
  };
};

const captureProfileService = new CaptureProfileService();
export const useDropImageSettings = () => {
  let imageSettingsPills = [];
  let imageSettingText = '';
  const imageSettingsList = [];
  const regionIds: number[] = useCurrentRegionIds();
  const experimentImagingDisabled = useExperimentImagingDisabledState();
  const { dropNumber } = useSelectedDropImageParameters() || {};
  const isWellImagingEnabled = useIsWellImagingEnabled(dropNumber);
  const disableDropImageSettings = experimentImagingDisabled || !isWellImagingEnabled;
  const expId = useRMSelector(selectExpId);
  const regionCaptureProfiles = useRMSelector(selectExpRegionCaptureProfiles);
  const updateRegionCaptureProfiles = useUpdateExpRegionCaptureProfiles();
  const filteredRegionCaptureProfiles = regionCaptureProfiles?.filter((profile) =>
    regionIds.includes(profile.regionId),
  );

  if (!filteredRegionCaptureProfiles?.length) {
    imageSettingText = 'Same as Experiment';
  } else if (!filteredRegionCaptureProfiles?.some((frc) => frc.imagingEnabled)) {
    imageSettingText = 'None';
  } else {
    filteredRegionCaptureProfiles
      ?.filter((item) => item.imagingEnabled)
      .map((item) => {
        imageSettingsList.push(item.captureProfile.name);
      });
    imageSettingsPills = [...imageSettingsList].slice(0, 2);
  }

  const titleTip = imageSettingsList.join(', ');

  const setDefaultRegionCaptureProfile = async (value = '', isNone = false) => {
    const rcp = new DropImageSettingsDTO({
      expId: expId,
      regionIds: regionIds,
      isCustomized: false,
      isNone: isNone,
      isCustomizedDeleted: false,
      captureProfileName: value,
    });
    const resp = await captureProfileService.updateRegionCaptureProfile([rcp]);
    updateRegionCaptureProfiles(resp);
  };

  const deleteSingleDropSetting = async (value) => {
    const rcp = new DropImageSettingsDTO({
      expId: expId,
      regionIds: regionIds,
      isCustomized: true,
      isNone: false,
      isCustomizedDeleted: true,
      captureProfileName: value,
    });
    const resp = await captureProfileService.updateRegionCaptureProfile([rcp]);
    updateRegionCaptureProfiles(resp);
  };

  return {
    imageSettingsPills,
    imageSettingText,
    titleTip,
    setDefaultRegionCaptureProfile,
    deleteSingleDropSetting,
    disableDropImageSettings,
    imageSettingsList,
  };
};

export const useImageZoomVisibleArea = (): {
  imageZoomVisibleArea: IImageZoomDTO;
  setImageZoomVisibleArea: (param: IImageZoomDTO) => void;
} => {
  const imageZoomVisibleArea = useRMSelector(selectImageZoomVisibleArea);
  const dispatch = useRMDispatch();

  const setImageZoomVisibleArea = debounce((imageZoomDto: IImageZoomDTO) => {
    dispatch(setImageZoomDto(imageZoomDto));
  }, 1000);

  return { imageZoomVisibleArea, setImageZoomVisibleArea };
};

export const usePlateTuningWindow = (
  captureProfileId: number,
  experimentTuningValues: ExperimentTuningValuesDTO[] | undefined,
  plateImagesStructure: PlateImageStructure[],
) => {
  const dispatch = useRMDispatch();
  const [tuningWindow, setTuningWindow] = useState<ImageTuningValues>(null);

  useEffect(() => {
    const fetchTuningWindow = async () => {
      const tuningValues = experimentTuningValues?.find(
        (t) => t.captureProfileID === captureProfileId,
      );
      const imageStructure = plateImagesStructure.find((x) => x.imageCompositeKeyString);

      if (imageStructure) {
        const plateImage = getCompositeImageStoreImageByKey(
          imageStructure.imageCompositeKeyString!,
        );

        if (plateImage && plateImage !== 'NoImage' && captureProfileId) {
          let imageTuningWindow = getTuningWindowForPlate(
            tuningValues,
            plateImage,
            captureProfileId,
          );

          if (!imageTuningWindow) {
            await UpdateGlobalDataStoreVersion([CAPTUREPROFILETUNINGVALUESSTORE.name]);

            imageTuningWindow = await waitForValue({
              action: () => {
                const updatedTuningValues = experimentTuningValues?.find(
                  (t) => t.captureProfileID === captureProfileId,
                );

                return getTuningWindowForPlate(
                  updatedTuningValues,
                  plateImage,
                  captureProfileId,
                );
              },
            });
          }

          dispatch(setImageTuning(imageTuningWindow));
          setTuningWindow(imageTuningWindow);
        }
      }
    };

    fetchTuningWindow();
  }, [captureProfileId, plateImagesStructure, dispatch]);

  return tuningWindow;
};
